import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useBusinessContext } from "../../context/ModelContext";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import {
  Box,
  Divider,
  HStack,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
} from "@chakra-ui/react";
import { EditBusinessForm } from "../../forms/EditBusinessForm";
import { useToast } from "../../hooks/useToast";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { useAdminApiUpdateRequest } from "../../hooks/useApiUpdatedRequest";
import { PreviewButton } from "../../components/Utilities/PreviewButton";
import { CopyText } from "../../components/Utilities/CopyText";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { BusinessMenu } from "../../components/Business/BusinessMenu";
import DateUtil from "../../util/DateUtil";

interface PassConfigRenderProps {
  passConfigs: {
    [key: string]: {
      PassConfigurationId?: string | null | undefined;
      IsEnabled?: boolean | undefined;
      Description?: string | null | undefined;
      Test?: string | null | undefined;
      ApplyWhenTestFails?: boolean | undefined;
    };
  };
}

export const BusinessDetail = () => {
  const [searchParams] = useSearchParams();
  const [business, setBusiness] = useState(useBusinessContext());
  const apiClient = useAdminApiClient();
  const { showErrorToast, showSuccessToast } = useToast();
  const [editMode, setEditMode] = useState<boolean>(
    searchParams.get("edit") !== null &&
      (searchParams.get("edit") === "" || searchParams.get("edit") == "true")
      ? true
      : false
  );
  const navigate = useNavigate();

  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();

  const onSuccess = useCallback(
    (business) => {
      setBusiness(business);
      setEditMode(false);
      showSuccessToast("Business updated.");
    },
    [showSuccessToast, business]
  );

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(message ?? "Business update failed.");
    },
    [showErrorToast]
  );

  const deleteBusinessRequest = useAdminApiUpdateRequest(
    (apiClient, businessId: string) => apiClient.deleteBusiness(businessId),
    [business]
  );

  const onDeleteBusiness = useCallback(
    (business: CherryHubAdminApi.BusinessSummaryItem) =>
      showConfirmationDialog(
        "Delete Business?",
        `Are you sure you wish to delete this business: ${business.DisplayName}?`,
        "Delete"
      ).then(({ ok }) => {
        if (ok) {
          deleteBusinessRequest.request(business.Meta!.Id!);
        }
      }),
    [business]
  );

  useEffect(() => {
    if (deleteBusinessRequest.result) {
      if (deleteBusinessRequest.result.ok) {
        showSuccessToast("Business deleted.");
        navigate(`/admin/businesses`);
      } else {
        showErrorToast(
          deleteBusinessRequest.result.message ?? "Failed to delete business."
        );
      }
    }
  }, [deleteBusinessRequest.result]);

  const PassConfigSummary = (props: PassConfigRenderProps) => {
    let countEnabled = 0;
    if (Object.keys(props.passConfigs).length === 0) return <></>;
    const configs = Object.keys(props.passConfigs).map((key) => {
      if (props.passConfigs[key].IsEnabled) countEnabled++;
    });
    return (
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>Status</Th>
            <Th>Count</Th>
          </Tr>
        </Thead>
        <Tbody>
          <Tr>
            <Td>Enabled</Td>
            <Td>{countEnabled}</Td>
          </Tr>
          <Tr>
            <Td>Disabled</Td>
            <Td>{configs.length - countEnabled}</Td>
          </Tr>
          <Tr>
            <Td>
              <b>Total</b>
            </Td>
            <Td>
              <b>{configs.length}</b>
            </Td>
          </Tr>
        </Tbody>
      </Table>
    );
  };

  const customerRiskManagementConfiguration =
    business.CustomerRiskManagementConfiguration;
  const bndryApiClientConfiguration =
    customerRiskManagementConfiguration?.BndryApiClientConfiguration;
  const au10TixApiClientConfiguration =
    customerRiskManagementConfiguration?.Au10TixApiClientConfiguration;
  const hasCustomerRiskManagementConfiguration =
    !!bndryApiClientConfiguration || !!au10TixApiClientConfiguration;

  return (
    <>
      <PageHeading>
        <PageHeading.Title>Business</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${business.BusinessId}`}
              >
                {editMode ? "Edit" : "Detail"}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          {editMode ? (
            <EditBusinessForm
              apiClient={apiClient}
              business={business}
              onCancel={() => setEditMode(false)}
              onSuccess={onSuccess}
              onFailure={onFailure}
            />
          ) : (
            <>
              <HStack w="100%" pb={4}>
                <Box mr="auto">
                  <CopyText text={business.BusinessId!} hover adjustMargin>
                    <Text fontSize="md" color="cherryUi.600">
                      {business.BusinessId}
                    </Text>
                  </CopyText>
                  <Text fontSize="xl" color="cherryUi.600">
                    <b>{business.DisplayName}</b>
                  </Text>
                </Box>
                <Box>
                  <BusinessMenu
                    businessId={business.Meta?.Id!}
                    onClick={() => setEditMode(true)}
                  />
                </Box>
              </HStack>
              <ViewControl
                label="Enabled :"
                value={business.Enabled ? "Yes" : "No"}
              />
              <ViewControl label="Licenced Products :">
                <UnorderedList flex={3} variant="empty" styleType="none">
                  {(business.LicencedCherryHubProducts?.length &&
                    business.LicencedCherryHubProducts?.map((product) => {
                      return (
                        <ListItem ml={4} key={product}>
                          {product == "CherryPay" ? (
                            <PreviewButton
                              url={`${CHERRYHUBPORTAL_URL}/businesses/${business.Meta?.Id}/members`}
                              adjustMargin
                            >
                              {product}
                            </PreviewButton>
                          ) : (
                            product
                          )}
                        </ListItem>
                      );
                    })) || <>-</>}
                </UnorderedList>
              </ViewControl>
              <ViewControl
                label="Active Member Filter Query"
                value={
                  business.DigitalMembershipConfiguration
                    ?.ActiveMemberFilterQuerystring
                }
              />
              <Box w="100%" pt={4} pb={4}>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Setting</Th>
                      <Th>Value</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <Td>SMS SenderId</Td>
                      <Td>{business.SmsSenderId ?? "-"}</Td>
                    </Tr>
                    <Tr>
                      <Td>Allow Digital Membership Card Creation</Td>
                      <Td>
                        {business.AllowDigitalMembershipCardCreation
                          ? "Yes"
                          : "No"}
                      </Td>
                    </Tr>
                    <Tr>
                      <Td>Digital Membership Card PassConfigurationId</Td>
                      <Td>
                        <PreviewButton
                          url={`/admin/businesses/${business.BusinessId}/pass-configurations/${business.BusinessId}.${business.DigitalMembershipCardPassConfigurationId}`}
                          linkProps={{ target: "_self" }}
                        >
                          {business.DigitalMembershipCardPassConfigurationId}
                        </PreviewButton>
                      </Td>
                    </Tr>
                    <Tr>
                      <Td>Default Apple Pass Type Identifier</Td>
                      <Td>{business.DefaultApplePassTypeIdentifier}</Td>
                    </Tr>
                    <Tr>
                      <Td>Default Apple Pass Type Identifier For Coupons</Td>
                      <Td>
                        {business.DefaultApplePassTypeIdentifierForCoupons ??
                          "-"}
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              </Box>
              <ViewControl
                label="Digital Membership Configurations"
                labelProps={{ flex: 1, fontSize: "md" }}
                value={
                  !business.DigitalMembershipConfiguration
                    ? "The digital membership configuration is not set yet!"
                    : ""
                }
                valueProps={{ flex: 3, fontStyle: "italic" }}
              />
              {business.DigitalMembershipConfiguration && (
                <>
                  <ViewControl
                    label="Expire All DMC After Date (UTC)"
                    value={
                      business.DigitalMembershipConfiguration
                        .ExpireAllDmcAfterDate
                        ? DateUtil.formatDate(
                            business.DigitalMembershipConfiguration
                              .ExpireAllDmcAfterDate,
                            "en-AU",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                              second: "2-digit",
                              hour12: true,
                              timeZone: "UTC",
                              timeZoneName: "short",
                            }
                          )
                        : "-"
                    }
                    pt={2}
                  />
                  <ViewControl
                    label="Obfuscate Member Values"
                    value={
                      business.DigitalMembershipConfiguration
                        .ObfuscateMemberValues
                        ? "Yes"
                        : "No"
                    }
                  />
                  {business.DigitalMembershipConfiguration
                    .PassConfigurationRules && (
                    <>
                      <ViewControl
                        label="Pass Configuration Rules"
                        value={
                          Object.keys(
                            business.DigitalMembershipConfiguration
                              .PassConfigurationRules
                          ).length === 0
                            ? "The pass configuration rules not set yet!"
                            : ""
                        }
                        valueProps={{ flex: 3, fontStyle: "italic" }}
                        pt={2}
                      />
                      <PassConfigSummary
                        passConfigs={
                          business.DigitalMembershipConfiguration
                            .PassConfigurationRules
                        }
                      />
                    </>
                  )}
                </>
              )}
              <ViewControl
                label="Customer Risk Management Configuration"
                labelProps={{ flex: 1, fontSize: "md" }}
                pt={4}
                value={
                  !hasCustomerRiskManagementConfiguration
                    ? "Customer risk management configuration has not been set."
                    : ""
                }
                valueProps={{ flex: 3, fontStyle: "italic" }}
              />
              {bndryApiClientConfiguration && (
                <>
                  <ViewControl
                    label="Bndry Client Id"
                    value={bndryApiClientConfiguration.ClientId}
                  />
                  <ViewControl
                    label="Bndry Log Requests"
                    value={
                      bndryApiClientConfiguration.LogRequests ? "Yes" : "No"
                    }
                  />
                  <ViewControl
                    label="Bndry API Base Url"
                    value={bndryApiClientConfiguration.ApiBaseUrl ?? "-"}
                  />
                </>
              )}
              {bndryApiClientConfiguration && au10TixApiClientConfiguration && (
                <Divider variant="section" />
              )}
              {au10TixApiClientConfiguration && (
                <>
                  <ViewControl
                    label="Au10Tix Key Id"
                    value={au10TixApiClientConfiguration.KeyId}
                  />
                  <ViewControl
                    label="Au10Tix Client Id"
                    value={au10TixApiClientConfiguration.ClientId}
                  />
                  <ViewControl
                    label="Au10Tix Log Requests"
                    value={
                      au10TixApiClientConfiguration.LogRequests ? "Yes" : "No"
                    }
                  />
                  <ViewControl
                    label="Au10Tix Auth API Base Url"
                    value={au10TixApiClientConfiguration.AuthApiBaseUrl ?? "-"}
                  />
                  <ViewControl
                    label="Au10Tix Use Auth API Base Url For All Requests"
                    value={
                      au10TixApiClientConfiguration.UseAuthApiBaseUrlForAllRequests
                        ? "Yes"
                        : "No"
                    }
                  />
                </>
              )}
            </>
          )}
        </VStack>
        {confirmationElement}
      </PageContent>
    </>
  );
};
